import type { Capitolo } from '../tipi.ts'

const capitolo: Capitolo = {
  id: 'scheda',
  titolo: 'La scheda della persona',
  gruppo: 'Le schede delle persone',
  permesso: 'persona.cerca',
  aCheServe: 'La scheda raccoglie tutto quello che riguarda una persona: anagrafica, percorsi, mediazioni, '
    + 'documenti, area riservata e cronologia. Qui trovi come leggerla, modificarla e stamparla.',
  parti: [
    {
      titolo: 'La testa della scheda',
      testo: [
        'In alto leggi il nome, il numero di scheda e, per le schede importate, il codice del vecchio archivio '
          + '(per esempio «A100»), lo stesso dei fascicoli cartacei. Accanto: lo stato della scheda e, se ci '
          + 'sono, il lucchetto «Area riservata» e le etichette «Da verificare» o «Dati anonimizzati».',
        'I comandi compaiono solo se i tuoi incarichi li consentono: «Modifica la scheda», «Stampa la scheda», '
          + '«Fissa un ricontatto», «Fissa un appuntamento», «Scrivi email», «Riferimento per il messenger», «Registra '
          + 'intervento».',
      ],
      immagine: {
        file: 'scheda.webp', larghezza: 1200, altezza: 760,
        descrizione: 'La testa di una scheda inventata con lo stato, i comandi e le linguette.',
      },
    },
    {
      titolo: 'Scrivere un’email alla persona',
      permesso: ['posta.usa', 'posta.aggancia'],
      passi: [
        'Premi «Scrivi email» fra i comandi della scheda: si apre il messaggio della Posta, con l’email della persona '
          + 'in «A», se è nella scheda, e la tua firma.',
        'Il messaggio parte dalla tua casella. Per scrivere da una casella di gruppo o di progetto sceglila in «Da».',
        'Scrivi oggetto e testo, con grassetti, elenchi e collegamenti se servono, e premi «Invia».',
        'Dopo l’invio la conversazione si collega alla scheda: in «Posta» porta l’etichetta «scheda», e le risposte '
          + 'della persona arrivano nella stessa conversazione.',
      ],
      nota: 'Il comando non c’è in palestra, dove la posta non si usa, né sulle schede anonimizzate. Se chiudi senza '
        + 'inviare, il messaggio resta fra le bozze della Posta, non collegato alla scheda.',
    },
    {
      titolo: 'Le linguette',
      passi: [
        '«Anagrafica»: le sezioni del modulo, «Altri codici», vulnerabilità, categorie e, in fondo, i «Consensi».',
        '«Percorsi»: ricontatti e appuntamenti, poi i percorsi con i loro interventi.',
        '«Mediazioni»: le mediazioni programmate e svolte.',
        '«Documenti»: i documenti caricati e la foto del documento.',
        '«Area riservata»: le note più delicate, che si aprono scrivendo il motivo.',
        '«Cronologia»: tutto quello che è successo sulla scheda, con chi, quando ed esito.',
      ],
    },
    {
      titolo: 'Modificare la scheda',
      permesso: 'persona.modifica',
      passi: [
        'Premi «Modifica la scheda». Se mancano dati obbligatori, si apre «Completa la scheda»; altrimenti si apre '
          + 'la linguetta «Anagrafica» con i «Dati anagrafici» in modifica.',
        'Nella linguetta «Anagrafica» ogni riquadro ha il suo «Modifica»: si cambia una sezione alla volta, poi '
          + '«Salva» o «Annulla».',
        'Per spostare la scheda in un’altra sede premi «Cambia sede» nell’«Intestazione della scheda». La sede '
          + 'decide chi vede la scheda.',
      ],
      nota: '«Formazione – Lavoro» e «Condizione abitativa e familiare» non si sovrascrivono: ogni salvataggio '
        + 'aggiunge una rilevazione con la data di oggi.',
    },
    {
      titolo: 'Documenti e foto del documento',
      permesso: 'documento.carica',
      passi: [
        'Linguetta «Documenti»: premi «Scegli un file» oppure «Fotografa il documento» (dal telefono si apre la '
          + 'fotocamera).',
        'Scegli il «Tipo di documento», la data e, se il documento scade, la «Scadenza».',
        'Premi «Carica».',
      ],
      nota: 'Il file è salvato subito ma resta «In verifica antivirus»: in orario di servizio si scarica entro '
        + 'un’ora circa; di sera e la domenica dopo il controllo della notte.',
    },
    {
      titolo: 'Stampare la scheda e scaricare il PDF',
      permesso: 'persona.identificativi',
      passi: [
        'Premi «Stampa la scheda». Si apre l’anteprima del foglio da far firmare.',
        'In «Opzioni di stampa» scegli il «Progetto in intestazione», i «Loghi nell’intestazione» e se compilare '
          + 'la riga del firmatario.',
        'Premi «Stampa» per la stampante, oppure «Scarica PDF» per un PDF da conservare.',
        'Il PDF lo prepara il gestionale da solo, in orario di servizio entro un’ora circa. Quando è pronto, '
          + 'arriva la notifica «Il PDF della scheda è pronto» e nel riquadro «PDF/A generati dal sistema» '
          + 'compare «Scarica».',
      ],
      immagine: {
        file: 'stampa.webp', larghezza: 1200, altezza: 760,
        descrizione: 'La pagina Stampa della scheda utente con il riquadro Opzioni di stampa e l’anteprima del foglio.',
      },
      nota: 'Le caselle dei consensi si stampano sempre vuote: la scelta la fa la persona sul foglio. Il PDF '
        + 'resta disponibile sette giorni, solo per chi l’ha chiesto.',
    },
    {
      titolo: 'Stampare il modulo del progetto',
      permesso: 'persona.leggi',
      passi: [
        'Servono due firme: il modulo del progetto, con il titolare del progetto, e il modulo CIDIS dei consensi. '
          + 'Il riquadro «Moduli dei progetti» della scheda ha una riga per ciascuno: «firmato il …» o «da firmare».',
        'Premi «Stampa i due moduli»: escono il modulo del progetto e poi il modulo CIDIS, compilati con i dati della '
          + 'scheda, nella stessa stampa o nello stesso PDF. Ogni riga ha anche il suo «Stampa».',
        'I campi che mancano restano da scrivere a mano. Falli firmare tutti e due.',
        'Carica il modulo del progetto firmato con «Carica firmato» sulla sua riga, con la data della firma. Per il '
          + 'modulo CIDIS registra i consensi e carica la scansione dalla sua riga, o dalla sezione «Consensi».',
      ],
      nota: 'Finché manca il modulo del progetto firmato, la scheda lo ricorda fra i dati consigliati; gli interventi '
        + 'si registrano lo stesso, servono solo i consensi 1 e 2. Le schede con «Dati anonimizzati» non hanno moduli.',
    },
  ],
  avvertenze: [
    {
      quando: '«Puoi solo consultare questa scheda: per modificarla serve un incarico di Operatore interno o di '
        + 'Project Manager…»',
      cosaFare: 'Ti manca un incarico sulla sede, sull’area o sul progetto della scheda: chiedilo a chi assegna '
        + 'gli incarichi.',
    },
    {
      quando: '«Scheda dell’archivio precedente senza i consensi della Scheda utente»',
      cosaFare: 'Puoi lavorare, ma al primo contatto stampa la scheda, falla firmare e registra i consensi.',
    },
    {
      quando: 'Il documento caricato non si scarica.',
      cosaFare: 'È in verifica antivirus: la riga dice quando sarà disponibile. Se il giorno dopo non lo è '
        + 'ancora, avvisa l’amministratore.',
    },
    {
      quando: 'Il PDF dice «Non riuscito: chiedine uno nuovo».',
      cosaFare: 'Premi di nuovo «Scarica PDF».',
    },
  ],
  vediAnche: ['consensi', 'percorsi-interventi', 'area-riservata', 'nuova-scheda'],
  parole: ['linguette', 'anagrafica', 'cronologia', 'documenti', 'foto', 'stampa', 'pdf', 'modifica', 'sede'],
}

export default capitolo
